import React, { useState } from 'react';
import classnames from 'classnames';
import './Icon.css';
import { IconName } from './IconName';
import GlobalProps from '../globalProps';
import { Spinner } from '../Spinner/Spinner';
import { Tooltip } from '../Tooltip/Tooltip';

export interface Props extends GlobalProps {
  name: IconName;
  title?: string;
  tooltip?: string;
  onClick?: () => void | Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  active?: boolean;
  attached?: boolean;
  color?: string;
  tooltipDarkMode?: boolean;
  size?: number;
}

export const icons: Record<string, string> = {};
const files = import.meta.glob('./icons/*.svg', {
  query: '?raw',
  eager: true,
  import: 'default',
});
Object.keys(files).forEach((key) => {
  const name = key.match(/^\.\/icons\/(.+)\.svg$/);
  if (name) {
    icons[name[1]] = files[key];
  }
});

export function Icon({
  name,
  loading,
  disabled,
  className,
  style,
  onClick,
  title,
  active,
  attached,
  tooltip,
  color = 'currentColor',
  tooltipDarkMode = true,
  size,
}: Props): JSX.Element {
  const [clickLoading, setLoading] = useState(false);

  if (loading || clickLoading) {
    return (
      <Spinner
        className={classnames('icon', `icon--${name}`, className, {
          'icon--active': active,
          'icon--attached': attached,
        })}
        style={style}
        scale="0.75"
      />
    );
  }

  const icon = (
    <span
      onClick={(event) => {
        if (onClick && !disabled && !loading) {
          event.stopPropagation();
          event.preventDefault();
          const result = onClick();
          if (result) {
            setLoading(true);
            result.finally(() => setLoading(false));
          }
        }
      }}
      title={title}
      className={classnames('icon', `icon--${name}`, className, {
        'icon--disabled': disabled,
        'icon--active': active,
        'icon--attached': attached,
        'icon--clickable': onClick,
      })}
      style={{ ...style, '--icon-size': size ? `${size}px` : undefined } as React.CSSProperties}
      dangerouslySetInnerHTML={{
        __html: icons[name].replace(/#[0-9A-Za-z]{6}/g, color),
      }}
    />
  );

  return tooltip ? (
    <Tooltip reference={icon} darkMode={tooltipDarkMode} position="top" maxWidth>
      {tooltip}
    </Tooltip>
  ) : (
    icon
  );
}
